'use client';

import { useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth-context';
import { updateOnboardingStatus } from '@/lib/api';

function waitForElement(selector: string, timeoutMs = 5000): Promise<Element | null> {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      if (Date.now() - start > timeoutMs) return resolve(null);
      requestAnimationFrame(check);
    };
    check();
  });
}

export function useGuidedTour() {
  const router = useRouter();
  const { refreshAccessToken } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const driverRef = useRef<any>(null);

  const startTour = useCallback(async () => {
    if (typeof window === 'undefined') return;

    const { driver } = await import('driver.js');

    const markComplete = async () => {
      try {
        await updateOnboardingStatus(true);
        await refreshAccessToken();
      } catch {
        // continue even if API fails
      }
    };

    let clickCleanup: (() => void) | null = null;

    const driverObj = driver({
      showProgress: true,
      animate: true,
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: 'sigalmark-tour',
      allowClose: true,
      disableActiveInteraction: false,
      doneBtnText: 'Finish',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      onHighlighted: (element) => {
        // Clean up previous listener
        if (clickCleanup) {
          clickCleanup();
          clickCleanup = null;
        }
        // Attach click-to-advance on highlighted elements
        if (element) {
          const handler = () => {
            driverObj.moveNext();
          };
          element.addEventListener('click', handler, { once: true });
          clickCleanup = () => element.removeEventListener('click', handler);
        }
      },
      onDestroyStarted: () => {
        if (clickCleanup) clickCleanup();
        markComplete();
        driverObj.destroy();
      },
      steps: [
        // 1. Welcome
        {
          popover: {
            title: 'Welcome to Sigalmark',
            description:
              'A two-minute tour of how you issue a certificate and how anyone verifies it. You can leave at any time and replay this later from your Profile.',
          },
        },
        // 2. The Certificates hub
        {
          element: '[data-tour="sidebar-certificates"]',
          onHighlightStarted: () => {
            const btn = document.querySelector(
              '[data-tour="sidebar-certificates"]',
            ) as HTMLElement | null;
            if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click();
          },
          popover: {
            title: 'Certificates',
            description:
              'Everything lives here: <b>All</b> to browse what you have issued, <b>Issue</b> to create new ones, <b>Analytics</b> for the numbers, and <b>Verify</b> to check any certificate.',
            side: 'right' as const,
          },
        },
        // 3. Go to Issue. Anchored on the parent: the sub-items are only
        //    rendered once a /certificates route is active, so "Issue" is not
        //    in the DOM yet while we are still on the home page.
        {
          element: '[data-tour="sidebar-certificates"]',
          popover: {
            title: 'Issue a certificate',
            description: "Let's create one now.",
            side: 'right' as const,
            onNextClick: () => {
              router.push('/certificates?tab=create');
              waitForElement('[data-tour="field-name"]', 8000).then((el) => {
                if (el) driverObj.moveNext();
                else driverObj.moveTo(9); // skip to the closing step
              });
            },
          },
        },
        // 4. Name
        {
          element: '[data-tour="field-name"]',
          popover: {
            title: 'Who it is for',
            description:
              'The company or person being certified. Use their full legal name: certificates are minted on the blockchain and a typo can only be fixed by revoking and re-issuing.',
            side: 'bottom' as const,
          },
        },
        // 5. Competency
        {
          element: '[data-tour="field-competency"]',
          popover: {
            title: 'What they achieved',
            description:
              "The qualification — e.g. 'First Aid', 'ISO 9001' or 'Fire Safety'.",
            side: 'bottom' as const,
          },
        },
        // 6. Expiry
        {
          element: '[data-tour="field-expiry"]',
          popover: {
            title: 'When it expires',
            description:
              'We remind the holder by email before and after this date. You can extend it later, even once it has expired.',
            side: 'bottom' as const,
          },
        },
        // 7. Issue
        {
          element: '[data-tour="btn-add-certificate"]',
          popover: {
            title: 'Issue it',
            description:
              'This mints the certificate on the blockchain and gives it a public verification page with its own QR code.',
            side: 'top' as const,
          },
        },
        // 8. Batch
        {
          element: '[data-tour="batch-section"]',
          popover: {
            title: 'Or do 25 at once',
            description:
              'Upload a CSV to issue up to 25 certificates in one go. Download the template from this tab; rows are checked before anything is minted.',
            side: 'bottom' as const,
          },
        },
        // 9. Verify
        {
          element: '[data-tour="sidebar-verify"]',
          onHighlightStarted: () => {
            const btn = document.querySelector(
              '[data-tour="sidebar-certificates"]',
            ) as HTMLElement | null;
            if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click();
          },
          popover: {
            title: 'Anyone can verify',
            description:
              'Paste a certificate link here to check it. Recipients and auditors can use this page too — no account needed.',
            side: 'right' as const,
          },
        },
        // 10. Close
        {
          popover: {
            title: "You're all set",
            description:
              'One last thing: on a certificate, <b>Download certificate</b> gives you the shareable image (your logo + its QR code), <b>Download QR code</b> gives the code on its own. Full guides are in the docs, and you can replay this tour from your Profile.',
          },
        },
      ],
    });

    driverRef.current = driverObj;
    driverObj.drive();
  }, [router, refreshAccessToken]);

  return { startTour };
}
